export type ExchangeInputMode = "typed" | "speech";

export interface ExchangeTimingMetadata {
  inputMode: ExchangeInputMode;
  inputStartedAt: number;
  inputCompletedAt: number;
  transcriptionStartedAt?: number;
  transcriptionCompletedAt?: number;
}

export interface BackendLLMTimings {
  timingSchemaVersion?: number;
  llmPipelineElapsedMs?: number;
  llm1CompletedOffsetMs?: number;
  llm2CompletedOffsetMs?: number;
  llm1ActualCompletedToLlm2CompletedMs?: number;
  llm1ResultAvailableToLlm2CompletedMs?: number;
  semanticAnswerId?: string;
  semanticAnswerSource?: string;
  semanticMatchMethod?: string;
  responseSource?: "semantic_cache" | "llm";
  responseReadyMs?: number;
  semanticTotalMs?: number;
  backendTurnToDispatchMs?: number;
  llm1Ms?: number;
  llm2Ms?: number;
  llmFastPath?: string;
  llm1Skipped?: boolean;
  llm2Skipped?: boolean;
  llm1RequestToStreamOpenMs?: number;
  llm1StreamOpenToFirstTokenMs?: number;
  llm1RequestToFirstTokenMs?: number;
  llm1FirstTokenToCompleteMs?: number;
  llm1TotalMs?: number;
  llm1PromptTokens?: number;
  llm1CompletionTokens?: number;
  llm1CachedPromptTokens?: number;
  llm1CacheWritePromptTokens?: number;
  llm1FinishReason?: string;
  llm1Model?: string;
  llm1Provider?: string;
  llm1ServiceTierActual?: string;
  llm1ReasoningEffortRequested?: string;
  llm1SpeculativeStarted?: boolean;
  llm1SpeculativeAccepted?: boolean;
  llm1SpeculativeDiscardReason?: string;
  llm1SpeculativeStartToLlm2StartMs?: number;
  llm1SpeculativeRemainingWaitMs?: number;
  llm1SpeculativeEstimatedTimeSavedMs?: number;
  llm1SpeculativeReadyBeforeLlm2StartMs?: number;
  llm1SpeculativeTranscriptLength?: number;
  llm2SpeculativeStarted?: boolean;
  llm2SpeculativeAccepted?: boolean;
  llm2SpeculativeDiscardReason?: string;
  llm2SpeculativeRemainingWaitMs?: number;
  llm2SpeculativeEstimatedTimeSavedMs?: number;
  llm2RequestToStreamOpenMs?: number;
  llm2StreamOpenToFirstTokenMs?: number;
  llm2RequestToFirstTokenMs?: number;
  llm2FirstTokenToCompleteMs?: number;
  llm2TotalMs?: number;
  llm2PromptTokens?: number;
  llm2CompletionTokens?: number;
  llm2CachedPromptTokens?: number;
  llm2CacheWritePromptTokens?: number;
  llm2FinishReason?: string;
  llm2Model?: string;
  llm2Provider?: string;
  llm2ServiceTierActual?: string;
  llm2ReasoningEffortRequested?: string;
}

export type AvatarSpeechType = "filler" | "patient_response";

export interface StartExchangeTimingInput extends ExchangeTimingMetadata {
  sessionId: string;
  caseId: string;
  fieldId?: string;
  question: string;
  avatarEnabled: boolean;
  avatarConnected: boolean;
}

interface ExchangeTimingRecord extends StartExchangeTimingInput {
  exchangeId: string;
  websocketSentAt: number;
  fillerReceivedAt?: number;
  fillerAvatarSpeakRequestedAt?: number;
  fillerAvatarSpeakingStartedAt?: number;
  fillerLength?: number;
  fillerAvatarSpeakFailed?: boolean;
  patientResponseReceivedAt?: number;
  avatarSpeakRequestedAt?: number;
  avatarSpeakingStartedAt?: number;
  responseLength?: number;
  backendTimings?: BackendLLMTimings;
  captured: boolean;
}

type AvatarCompletionReason =
  | "avatar_started"
  | "avatar_not_connected"
  | "avatar_speak_failed"
  | "avatar_start_timeout"
  | "no_patient_response"
  | "websocket_error";

interface PendingAvatarSpeechRequest {
  exchangeId: string;
  speechType: AvatarSpeechType;
}

function now(): number {
  return performance.now();
}

function roundDuration(value: number | undefined): number | undefined {
  if (value === undefined || !Number.isFinite(value)) {
    return undefined;
  }

  return Math.max(0, Math.round(value));
}

function createExchangeId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `exchange_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function cleanProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(
      ([, value]) => value !== undefined && value !== null,
    ),
  );
}

class ExchangeTimingService {
  private records = new Map<string, ExchangeTimingRecord>();
  private avatarStartTimeouts = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private fillerStartTimeouts = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private pendingAvatarSpeechRequests: PendingAvatarSpeechRequest[] = [];

  startExchange(input: StartExchangeTimingInput): string {
    const exchangeId = createExchangeId();

    this.records.set(exchangeId, {
      ...input,
      exchangeId,
      websocketSentAt: now(),
      captured: false,
    });

    return exchangeId;
  }

  markPatientResponseReceived(
    exchangeId: string | undefined,
    response: string,
    backendTimings?: BackendLLMTimings,
  ): string | undefined {
    const record = exchangeId
      ? this.records.get(exchangeId)
      : this.getMostRecentOpenRecord();
    if (!record || record.captured) {
      return undefined;
    }

    record.patientResponseReceivedAt = now();
    record.responseLength = response.length;
    record.backendTimings = backendTimings;

    return record.exchangeId;
  }

  completeIfAvatarWasNotConnected(exchangeId: string | undefined): void {
    if (!exchangeId) {
      return;
    }

    const record = this.records.get(exchangeId);
    if (!record || record.captured || record.avatarConnected) {
      return;
    }

    this.completeWithoutAvatar(exchangeId, "avatar_not_connected");
  }

  markFillerReceived(exchangeId: string | undefined, filler: string): void {
    if (!exchangeId) {
      return;
    }

    const record = this.records.get(exchangeId);
    if (!record || record.captured) {
      return;
    }

    record.fillerReceivedAt = now();
    record.fillerLength = filler.length;
  }

  markAvatarSpeakRequested(
    exchangeId: string | undefined,
    speechType: AvatarSpeechType,
  ): void {
    if (!exchangeId) {
      return;
    }

    const record = this.records.get(exchangeId);
    if (!record || record.captured) {
      return;
    }

    const requestedAt = now();
    this.pendingAvatarSpeechRequests.push({ exchangeId, speechType });

    if (speechType === "filler") {
      record.fillerAvatarSpeakRequestedAt = requestedAt;
      this.setFillerStartTimeout(exchangeId);
      return;
    }

    record.avatarSpeakRequestedAt = requestedAt;

    const existingTimeout = this.avatarStartTimeouts.get(exchangeId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    this.avatarStartTimeouts.set(
      exchangeId,
      setTimeout(() => {
        this.completeWithoutAvatar(exchangeId, "avatar_start_timeout");
      }, 15000),
    );
  }

  markAvatarSpeakingStarted(exchangeId?: string, speechType?: AvatarSpeechType): void {
    const index = exchangeId
      ? this.pendingAvatarSpeechRequests.findIndex(request =>
          request.exchangeId === exchangeId && request.speechType === speechType)
      : 0;
    if (index < 0) return;
    let request: PendingAvatarSpeechRequest | undefined = this.pendingAvatarSpeechRequests.splice(index, 1)[0];
    let record = request ? this.records.get(request.exchangeId) : undefined;

    while (request && (!record || record.captured)) {
      if (exchangeId) return;
      request = this.pendingAvatarSpeechRequests.shift();
      record = request ? this.records.get(request.exchangeId) : undefined;
    }

    if (!request || !record) {
      return;
    }

    if (request.speechType === "filler") {
      record.fillerAvatarSpeakingStartedAt = now();
      this.clearFillerStartTimeout(request.exchangeId);
      return;
    }

    record.avatarSpeakingStartedAt = now();
    this.clearAvatarStartTimeout(request.exchangeId);
    this.capture(record, "avatar_started");
  }

  markAvatarSpeakFailed(
    exchangeId: string | undefined,
    speechType: AvatarSpeechType,
  ): void {
    if (!exchangeId) {
      return;
    }

    this.removePendingAvatarSpeechRequest(exchangeId, speechType);

    if (speechType === "filler") {
      const record = this.records.get(exchangeId);
      if (record && !record.captured) {
        record.fillerAvatarSpeakFailed = true;
      }
      this.clearFillerStartTimeout(exchangeId);
      return;
    }

    this.completeWithoutAvatar(exchangeId, "avatar_speak_failed");
  }

  completeWithoutAvatar(
    exchangeId: string | undefined,
    reason: Exclude<AvatarCompletionReason, "avatar_started">,
  ): void {
    if (!exchangeId) {
      return;
    }

    this.clearAvatarStartTimeout(exchangeId);
    this.clearFillerStartTimeout(exchangeId);
    this.removePendingAvatarSpeechRequests(exchangeId);

    const record = this.records.get(exchangeId);
    if (!record || record.captured) {
      return;
    }

    this.capture(record, reason);
  }

  completeMostRecentWithoutAvatar(
    reason: Exclude<AvatarCompletionReason, "avatar_started">,
  ): void {
    const openRecord = this.getMostRecentOpenRecord();

    if (openRecord) {
      this.completeWithoutAvatar(openRecord.exchangeId, reason);
    }
  }

  private getMostRecentOpenRecord(): ExchangeTimingRecord | undefined {
    return [...this.records.values()]
      .filter((record) => !record.captured)
      .sort((a, b) => b.websocketSentAt - a.websocketSentAt)[0];
  }

  private capture(
    record: ExchangeTimingRecord,
    reason: AvatarCompletionReason,
  ): void {
    record.captured = true;

    const inputStoppedToWebsocketSentMs =
      roundDuration(record.websocketSentAt - record.inputCompletedAt) || 0;
    const inputStoppedToAvatarFillerStartedMs =
      record.fillerAvatarSpeakingStartedAt
        ? roundDuration(
            record.fillerAvatarSpeakingStartedAt - record.inputCompletedAt,
          )
        : undefined;
    const inputStoppedToAvatarPatientResponseStartedMs =
      record.avatarSpeakingStartedAt
        ? roundDuration(
            record.avatarSpeakingStartedAt - record.inputCompletedAt,
          )
        : undefined;
    const responseReceivedToAvatarStartedMs =
      record.avatarSpeakingStartedAt !== undefined &&
      record.patientResponseReceivedAt !== undefined
        ? roundDuration(
            record.avatarSpeakingStartedAt - record.patientResponseReceivedAt,
          )
        : undefined;
    // Preserve the requested legacy metric as a receipt-anchored estimate. Delivery time is unknown.
    const llm2CompletedToDispatchMs =
      record.backendTimings?.llm2CompletedOffsetMs !== undefined &&
      record.backendTimings?.backendTurnToDispatchMs !== undefined
        ? roundDuration(
            record.backendTimings.backendTurnToDispatchMs -
              record.backendTimings.llm2CompletedOffsetMs,
          )
        : undefined;
    const llm2ToAvatarEstimate =
      record.backendTimings?.responseSource === "llm" &&
      responseReceivedToAvatarStartedMs !== undefined &&
      llm2CompletedToDispatchMs !== undefined
        ? responseReceivedToAvatarStartedMs + llm2CompletedToDispatchMs
        : undefined;

    const properties = cleanProperties({
      exchange_id: record.exchangeId,
      session_id: record.sessionId,
      case_id: record.caseId,
      field_id: record.fieldId,
      input_mode: record.inputMode,
      input_stopped_to_websocket_sent_ms: inputStoppedToWebsocketSentMs,
      websocket_sent_to_patient_response_received_ms:
        record.patientResponseReceivedAt !== undefined
          ? roundDuration(
              record.patientResponseReceivedAt - record.websocketSentAt,
            )
          : undefined,
      input_stopped_to_patient_response_received_ms:
        record.patientResponseReceivedAt !== undefined
          ? roundDuration(
              record.patientResponseReceivedAt - record.inputCompletedAt,
            )
          : undefined,
      patient_response_received_to_avatar_speak_requested_ms:
        record.avatarSpeakRequestedAt !== undefined &&
        record.patientResponseReceivedAt !== undefined
          ? roundDuration(
              record.avatarSpeakRequestedAt - record.patientResponseReceivedAt,
            )
          : undefined,
      avatar_speak_requested_to_avatar_patient_response_started_ms:
        record.avatarSpeakingStartedAt !== undefined &&
        record.avatarSpeakRequestedAt !== undefined
          ? roundDuration(
              record.avatarSpeakingStartedAt - record.avatarSpeakRequestedAt,
            )
          : undefined,
      completion_reason: reason,
      question_length: record.question.length,
      filler_length: record.fillerLength,
      response_length: record.responseLength,
      avatar_enabled: record.avatarEnabled,
      avatar_connected_at_send: record.avatarConnected,
      filler_avatar_speak_failed: record.fillerAvatarSpeakFailed,
      timing_schema_version: 2,
      patient_response_received_to_avatar_patient_response_started_ms:
        responseReceivedToAvatarStartedMs,
      llm_response_2_finished_to_avatar_patient_response_started_ms:
        llm2ToAvatarEstimate,
      llm_response_2_finished_to_avatar_patient_response_started_is_estimate:
        llm2ToAvatarEstimate !== undefined ? true : undefined,
      input_stopped_to_avatar_filler_started_ms:
        inputStoppedToAvatarFillerStartedMs,
      input_stopped_to_avatar_patient_response_started_ms:
        inputStoppedToAvatarPatientResponseStartedMs,
      llm_fast_path: record.backendTimings?.llmFastPath,
      llm1_skipped: record.backendTimings?.llm1Skipped,
      llm2_skipped: record.backendTimings?.llm2Skipped,
      llm1_request_to_stream_open_ms: roundDuration(
        record.backendTimings?.llm1RequestToStreamOpenMs,
      ),
      llm1_stream_open_to_first_token_ms: roundDuration(
        record.backendTimings?.llm1StreamOpenToFirstTokenMs,
      ),
      llm1_request_to_first_token_ms: roundDuration(
        record.backendTimings?.llm1RequestToFirstTokenMs,
      ),
      llm1_first_token_to_complete_ms: roundDuration(
        record.backendTimings?.llm1FirstTokenToCompleteMs,
      ),
      llm1_total_ms: roundDuration(record.backendTimings?.llm1TotalMs),
      llm1_prompt_tokens: roundDuration(
        record.backendTimings?.llm1PromptTokens,
      ),
      llm1_completion_tokens: roundDuration(
        record.backendTimings?.llm1CompletionTokens,
      ),
      llm1_cached_prompt_tokens: roundDuration(
        record.backendTimings?.llm1CachedPromptTokens,
      ),
      llm1_cache_write_prompt_tokens: roundDuration(
        record.backendTimings?.llm1CacheWritePromptTokens,
      ),
      llm1_finish_reason: record.backendTimings?.llm1FinishReason,
      llm1_model: record.backendTimings?.llm1Model,
      llm1_provider: record.backendTimings?.llm1Provider,
      llm1_service_tier_actual: record.backendTimings?.llm1ServiceTierActual,
      llm1_reasoning_effort_requested:
        record.backendTimings?.llm1ReasoningEffortRequested,
      llm1_speculative_started: record.backendTimings?.llm1SpeculativeStarted,
      llm1_speculative_accepted: record.backendTimings?.llm1SpeculativeAccepted,
      llm1_speculative_discard_reason:
        record.backendTimings?.llm1SpeculativeDiscardReason,
      llm1_speculative_start_to_llm2_start_ms: roundDuration(
        record.backendTimings?.llm1SpeculativeStartToLlm2StartMs,
      ),
      llm1_speculative_remaining_wait_ms: roundDuration(
        record.backendTimings?.llm1SpeculativeRemainingWaitMs,
      ),
      llm1_speculative_estimated_time_saved_ms: roundDuration(
        record.backendTimings?.llm1SpeculativeEstimatedTimeSavedMs,
      ),
      llm1_speculative_ready_before_llm2_start_ms: roundDuration(
        record.backendTimings?.llm1SpeculativeReadyBeforeLlm2StartMs,
      ),
      llm1_speculative_transcript_length: roundDuration(
        record.backendTimings?.llm1SpeculativeTranscriptLength,
      ),
      llm2_speculative_started: record.backendTimings?.llm2SpeculativeStarted,
      llm2_speculative_accepted: record.backendTimings?.llm2SpeculativeAccepted,
      llm2_speculative_discard_reason:
        record.backendTimings?.llm2SpeculativeDiscardReason,
      llm2_speculative_remaining_wait_ms: roundDuration(
        record.backendTimings?.llm2SpeculativeRemainingWaitMs,
      ),
      llm2_speculative_estimated_time_saved_ms: roundDuration(
        record.backendTimings?.llm2SpeculativeEstimatedTimeSavedMs,
      ),
      llm2_request_to_stream_open_ms: roundDuration(
        record.backendTimings?.llm2RequestToStreamOpenMs,
      ),
      llm2_stream_open_to_first_token_ms: roundDuration(
        record.backendTimings?.llm2StreamOpenToFirstTokenMs,
      ),
      llm2_request_to_first_token_ms: roundDuration(
        record.backendTimings?.llm2RequestToFirstTokenMs,
      ),
      llm2_first_token_to_complete_ms: roundDuration(
        record.backendTimings?.llm2FirstTokenToCompleteMs,
      ),
      llm2_total_ms: roundDuration(record.backendTimings?.llm2TotalMs),
      llm2_prompt_tokens: roundDuration(
        record.backendTimings?.llm2PromptTokens,
      ),
      llm2_completion_tokens: roundDuration(
        record.backendTimings?.llm2CompletionTokens,
      ),
      llm2_cached_prompt_tokens: roundDuration(
        record.backendTimings?.llm2CachedPromptTokens,
      ),
      llm2_cache_write_prompt_tokens: roundDuration(
        record.backendTimings?.llm2CacheWritePromptTokens,
      ),
      llm2_finish_reason: record.backendTimings?.llm2FinishReason,
      llm2_model: record.backendTimings?.llm2Model,
      llm2_provider: record.backendTimings?.llm2Provider,
      llm2_service_tier_actual: record.backendTimings?.llm2ServiceTierActual,
      llm2_reasoning_effort_requested:
        record.backendTimings?.llm2ReasoningEffortRequested,
    });
    console.info("Patient exchange latency breakdown", {
      ...properties,
      response_source: record.backendTimings?.responseSource,
      semantic_total_ms: roundDuration(record.backendTimings?.semanticTotalMs),
      response_ready_ms: roundDuration(record.backendTimings?.responseReadyMs),
      backend_turn_to_dispatch_ms: roundDuration(
        record.backendTimings?.backendTurnToDispatchMs,
      ),
    });
    this.sendExchangeTimingToBackend(properties);

    this.records.delete(record.exchangeId);
  }

  private clearAvatarStartTimeout(exchangeId: string): void {
    const timeout = this.avatarStartTimeouts.get(exchangeId);
    if (!timeout) {
      return;
    }

    clearTimeout(timeout);
    this.avatarStartTimeouts.delete(exchangeId);
  }

  private setFillerStartTimeout(exchangeId: string): void {
    const existingTimeout = this.fillerStartTimeouts.get(exchangeId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    this.fillerStartTimeouts.set(
      exchangeId,
      setTimeout(() => {
        this.removePendingAvatarSpeechRequest(exchangeId, "filler");
        this.clearFillerStartTimeout(exchangeId);
      }, 15000),
    );
  }

  private clearFillerStartTimeout(exchangeId: string): void {
    const timeout = this.fillerStartTimeouts.get(exchangeId);
    if (!timeout) {
      return;
    }

    clearTimeout(timeout);
    this.fillerStartTimeouts.delete(exchangeId);
  }

  private removePendingAvatarSpeechRequest(
    exchangeId: string,
    speechType: AvatarSpeechType,
  ): void {
    const index = this.pendingAvatarSpeechRequests.findIndex(
      (request) =>
        request.exchangeId === exchangeId && request.speechType === speechType,
    );

    if (index >= 0) {
      this.pendingAvatarSpeechRequests.splice(index, 1);
    }
  }

  private removePendingAvatarSpeechRequests(exchangeId: string): void {
    this.pendingAvatarSpeechRequests = this.pendingAvatarSpeechRequests.filter(
      (request) => request.exchangeId !== exchangeId,
    );
  }

  private sendExchangeTimingToBackend(
    properties: Record<string, unknown>,
  ): void {
    const apiBaseUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
    const endpoint = `${apiBaseUrl.replace(/\/$/, "")}/api/analytics/exchange-timing`;
    const body = JSON.stringify(properties);

    void fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: body.length < 64000,
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          console.warn("Exchange timing event was rejected", {
            status: response.status,
            statusText: response.statusText,
          });
        }
      })
      .catch((error) => {
        console.warn("Failed to send exchange timing event", error);
      });
  }
}

export const exchangeTimingService = new ExchangeTimingService();
